import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import MenuIcon from "@mui/icons-material/Menu";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import {
  AppBar,
  Badge,
  Box,
  Button,
  Container,
  Menu,
  MenuItem,
  Modal,
  Toolbar,
  Typography,
} from "@mui/material";
import { useUserContext } from "context/userContext";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { modalStyle } from "styles/modalStyle";
import { useCart } from "../context/CartContext";
import { useModal } from "../context/ModalContext";
import LoginPage from "../pages/LoginPage";
import RequestFormGuest from "../pages/RequestFormGuest";
import ForgetPassword from "./login/forgetPassword/forgetPassword";

function NavBar() {
  const { isAuth, logout } = useUserContext();
  const [anchorEl, setAnchorEl] = useState(null);
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [signInOpen, setSignInOpen] = useState(false);
  const { isCheckoutOpen, setIsCheckoutOpen, setSignedInCheckoutOpen } =
    useModal();
  const navigate = useNavigate();
  const { cartItems, requestFormItems, emptyCart } = useCart();
  const totalItems = useMemo(() => {
    return isAuth
      ? cartItems.reduce((acc, item) => acc + item.quantity, 0)
      : requestFormItems.reduce((acc, item) => acc + item.quantity, 0);
  }, [isAuth, cartItems, requestFormItems]);

  const [toggleForgetPassForm, setToggleForgetPassForm] = useState(false);
  const location = useLocation();

  const handleMobileMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMobileMenuClose = () => setAnchorEl(null);
  const handleProfileMenuOpen = (event) =>
    setProfileAnchorEl(event.currentTarget);
  const handleProfileMenuClose = () => setProfileAnchorEl(null);
  const handleSignInOpen = () => setSignInOpen(true);
  const handleSignInClose = () => setSignInOpen(false);

  useEffect(() => {
    if (location.state?.openModal) {
      setSignInOpen(true);
    }
  }, [location.state]);

  const handleCartOpen = async () => {
    try {
      if (isAuth) {
        navigate("/Cart");
      } else {
        setIsCheckoutOpen(true);
      }
    } catch (error) {
      console.error("Failed to fetch cart:", error.message);
    }
  };

  const handleCartClose = () => {
    setIsCheckoutOpen(false);
    setSignedInCheckoutOpen(false);
  };

  const handleSignIn = () => {
    handleSignInClose();
  };

  const handleLogout = () => {
    logout();
    handleProfileMenuClose();
    navigate("/");
  };

  const handleNavigation = (path) => {
    navigate(path);
    handleMobileMenuClose();
  };

  const handleForgetPassForm = () => {
    setToggleForgetPassForm(!toggleForgetPassForm);
    handleSignInClose();
  };

  return (
    <>
      <AppBar
        position="sticky"
        sx={{
          top: 0,
          left: 0,
          right: 0,
          boxShadow: 0,
          bgcolor: "#424242",
          backgroundImage: "none",
        }}
      >
        <Container maxWidth="lg">
          <Toolbar
            variant="regular"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              bgcolor: "#424242",
              backdropFilter: "blur(24px)",
              minHeight: 60,
            }}
          >
            <Box sx={{ display: { xs: "flex", md: "none" } }}>
              {isAuth ? (
                <Button
                  variant="text"
                  aria-label="open menu"
                  onClick={handleMobileMenuOpen}
                  sx={{ minWidth: "30px", p: "4px", color: "#ffffff" }}
                >
                  <MenuIcon />
                </Button>
              ) : (
                <Button
                  variant="text"
                  sx={{ color: "#ffffff" }}
                  onClick={() => handleNavigation("/Filters")}
                >
                  Filter
                </Button>
              )}
            </Box>

            <Box sx={{ display: { xs: "none", md: "flex" } }}>
              <MenuItem
                sx={{ py: "6px", px: "12px" }}
                onClick={() => handleNavigation("/Filters")}
              >
                <Typography variant="body2" sx={{ color: "#ffffff" }}>
                  Filter
                </Typography>
              </MenuItem>
              {isAuth && (
                <>
                  <MenuItem
                    sx={{ py: "6px", px: "12px" }}
                    onClick={() => handleNavigation("/groups")}
                  >
                    <Typography variant="body2" sx={{ color: "#ffffff" }}>
                      Mine Maskiner
                    </Typography>
                  </MenuItem>
                  <MenuItem
                    sx={{ py: "6px", px: "12px" }}
                    onClick={() => handleNavigation("/Orders")}
                  >
                    <Typography variant="body2" sx={{ color: "#ffffff" }}>
                      Mine Ordrer
                    </Typography>
                  </MenuItem>
                </>
              )}
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 0.5,
                alignItems: "center",
                margin: "0",
                padding: "0",
              }}
            >
              {isAuth ? (
                <Button
                  variant="text"
                  size="small"
                  onClick={handleProfileMenuOpen}
                  sx={{ color: "#ffffff" }}
                >
                  <AccountCircleIcon />
                </Button>
              ) : (
                <Button
                  variant="text"
                  size="small"
                  onClick={handleSignInOpen}
                  sx={{ color: "#ffffff" }}
                >
                  Log ind
                </Button>
              )}
              <Button
                variant="text"
                size="small"
                onClick={handleCartOpen}
                sx={{ color: "#ffffff" }}
              >
                <Badge badgeContent={totalItems} color="secondary">
                  <ShoppingCartIcon />
                </Badge>
              </Button>
            </Box>

            {isAuth && (
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMobileMenuClose}
                PaperProps={{
                  style: {
                    maxHeight: "70vh",
                    width: "30ch",
                  },
                }}
              >
                <MenuItem onClick={() => handleNavigation("/Filters")}>
                  Filter
                </MenuItem>
                <MenuItem onClick={() => handleNavigation("/groups")}>
                  Mine Maskiner
                </MenuItem>
                <MenuItem onClick={() => handleNavigation("/Orders")}>
                  Mine Ordrer
                </MenuItem>
              </Menu>
            )}

            <Menu
              anchorEl={profileAnchorEl}
              open={Boolean(profileAnchorEl)}
              onClose={handleProfileMenuClose}
            >
              <MenuItem
                onClick={() => {
                  handleNavigation("/profile");
                  handleProfileMenuClose();
                }}
              >
                Min Info
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleNavigation("/profile/change-password");
                  handleProfileMenuClose();
                }}
              >
                Skift kodeord
              </MenuItem>
              <MenuItem onClick={handleLogout}>Log ud</MenuItem>
            </Menu>
          </Toolbar>
        </Container>
      </AppBar>

      <Modal open={signInOpen} onClose={handleSignInClose}>
        <Box sx={modalStyle}>
          <LoginPage
            onSignIn={handleSignIn}
            closeLoginForm={handleSignInClose}
            showForgetPassForm={handleForgetPassForm}
          />
        </Box>
      </Modal>
      <ForgetPassword
        display={toggleForgetPassForm}
        onClose={handleForgetPassForm}
        backToLogin={() => {
          setSignInOpen(true);
          setToggleForgetPassForm(false);
        }}
      />

      <Modal open={isCheckoutOpen} onClose={handleCartClose}>
        <Box sx={modalStyle}>
          <Typography
            id="modal-modal-title"
            variant="h6"
            component="h2"
            mb={2}
            alignContent={"center"}
            alignItems={"center"}
          >
            Forespørgsel til JL Filtersystem
          </Typography>
          <RequestFormGuest />
        </Box>
      </Modal>
    </>
  );
}
export default NavBar;
